import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useLanguage } from '../contexts/LanguageContext';
import { content } from '../data/content';
import { Handshake, Car, FileText, Shield, Users, TrendingUp } from 'lucide-react';

const HeroContainer = styled.section`
  position: relative;
  min-height: 100vh;
  min-height: -webkit-fill-available;
  background: url('/images/driving.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
  overflow: hidden;
  padding: 120px 40px 40px;
  
  @media (max-width: 768px) {
    min-height: calc(100vh - 80px);
    padding: 80px 20px 20px;
    overflow-y: auto;
  }
  
  @media (max-height: 600px) {
    min-height: 100vh;
    padding: 60px 20px 20px;
  }
`;

const HeroContent = styled.div`
  max-width: 1500px;
  margin: 0 auto;
  padding: 60px 70px;
  z-index: 4;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  background: rgba(255, 255, 255, 0.25);
  backdrop-filter: blur(3px);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(255, 255, 255, 0.1);
  opacity: 0;
  transform: translateY(-60px);
  animation: heroFadeIn 0.6s ease-out 0.2s forwards;

  @keyframes heroFadeIn {
    to {
      opacity: 1;
      transform: translateY(-60px);
    }
  }

  @media (max-width: 968px) {
    padding: 50px 50px;
    max-width: 1200px;
    transform: translateY(-45px);
    
    @keyframes heroFadeIn {
      to {
        opacity: 1;
        transform: translateY(-45px);
      }
    }
  }

  @media (max-width: 768px) {
    padding: 40px 35px;
    max-width: calc(100vw - 40px);
    border-radius: 8px;
    transform: translateY(-30px);
    
    @keyframes heroFadeIn {
      to {
        opacity: 1;
        transform: translateY(-30px);
      }
    }
  }
  
  @media (max-height: 600px) {
    padding: 30px 40px;
    transform: translateY(-22px);
    
    @keyframes heroFadeIn {
      to {
        opacity: 1;
        transform: translateY(-22px);
      }
    }
  }
`;

const HeroTitle = styled.h1`
  font-family: 'bahnschrift', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  color: #000000;
  letter-spacing: 1px;
  line-height: 1.1;
  text-shadow: none;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 2rem;
  color: #6d6d63;
  line-height: 1.3;
  text-shadow: 1px 1px 3px rgba(255, 255, 255, 0.8);

  @media (max-width: 768px) {
    font-size: 1.4rem;
  }
`;

const HeroDescription = styled.p`
  font-size: 1.2rem;
  font-weight: 700;
  line-height: 1.7;
  margin-bottom: 2.5rem;
  color: #333333;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  text-shadow: none;
`;

const HeroCTA = styled.button`
  background: #b7ab9f;
  color: #ffffff;
  border: 2px solid #b7ab9f;
  padding: 1.2rem 3rem;
  font-size: 1.1rem;
  font-weight: 700;
  border-radius: 3px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  margin-bottom: 1.5rem;

  &:hover {
    background: #6d6d63;
    color: #ffffff;
    border-color: #6d6d63;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
  }
`;

const HeroFeatures = styled.div`
  position: relative;
  background: rgba(0, 0, 0, 0.95);
  padding: 2rem 40px;
  margin: 0 -40px -40px;
  z-index: 4;
  backdrop-filter: blur(10px);
  opacity: 0;
  transform: translateY(20px);
  animation: featuresSlideUp 0.5s ease-out 0.4s forwards;

  @keyframes featuresSlideUp {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @media (max-width: 768px) {
    padding: 1.5rem 20px;
    margin: 0 -20px -20px;
    overflow: hidden;
  }
  
  @media (max-height: 600px) {
    padding: 1rem 20px;
    margin: 0 -20px -20px;
  }
`;

const FeaturesContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 3rem;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    padding: 0 15px;
  }
  
  @media (max-width: 768px) {
    gap: 1.5rem;
    padding: 0 10px;
  }
`;

const FeatureCircle = styled.div`
  width: 140px;
  height: 140px;
  background: linear-gradient(135deg, rgba(138, 138, 126, 0.15), rgba(138, 138, 126, 0.25));
  border: 3px solid rgba(138, 138, 126, 0.6);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  transition: all 0.4s ease;
  position: relative;
  overflow: hidden;
  box-shadow: 
    0 8px 25px rgba(0, 0, 0, 0.3),
    inset 0 2px 10px rgba(255, 255, 255, 0.1);

  @media (max-width: 768px) {
    width: 100px;
    height: 100px;
    margin-bottom: 1rem;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, transparent, rgba(255, 255, 255, 0.15), transparent);
    border-radius: 50%;
    opacity: 0.7;
  }

  &:hover {
    background: linear-gradient(135deg, rgba(138, 138, 126, 0.3), rgba(138, 138, 126, 0.4));
    transform: scale(1.05) translateY(-5px);
    box-shadow: 
      0 15px 35px rgba(0, 0, 0, 0.4),
      inset 0 2px 15px rgba(255, 255, 255, 0.2);
    border-color: rgba(138, 138, 126, 0.8);
  }

  svg {
    width: 45px;
    height: 45px;
    color: #b7ab9f;
    transition: all 0.3s ease;
    z-index: 3;
    position: relative;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
    
    @media (max-width: 768px) {
      width: 35px;
      height: 35px;
    }
  }

  &:hover svg {
    color: #ffffff;
    transform: scale(1.1);
  }

  img {
    width: 60px;
    height: 60px;
    object-fit: contain;
    transition: all 0.3s ease;
    z-index: 3;
    position: relative;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
    
    @media (max-width: 768px) {
      width: 45px;
      height: 45px;
    }
  }

  img[src="/images/clip.png"] {
    transform: translateX(9px);
  }

  &:hover img {
    transform: scale(1.1);
    filter: brightness(1.2) drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
  }

  &:hover img[src="/images/clip.png"] {
    transform: translateX(9px) scale(1.1);
  }
`;

const FeatureItem = styled.div`
  text-align: center;
  color: #ffffff;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
  }
`;

const FeatureTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 700;
  color: #b7ab9f;
  margin-bottom: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: color 0.3s ease;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);

  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 0.5rem;
  }

  ${FeatureItem}:hover & {
    color: #ffffff;
  }
`;

const FeatureDescription = styled.p`
  font-size: 1rem;
  color: #ffffff;
  opacity: 0.9;
  line-height: 1.5;
  transition: opacity 0.3s ease;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }

  ${FeatureItem}:hover & {
    opacity: 1;
  }
`;

const Hero = () => {
  const { language } = useLanguage();
  const t = content[language];

  const scrollToServices = () => {
    const element = document.getElementById('services');
    if (element) {
      const headerHeight = 120; // Account for sticky header height
      const elementPosition = element.offsetTop - headerHeight;
      
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
    }
  };

  const features = [
    {
      icon: <img src="/images/handshake.png" alt="Handshake" />,
      title: language === 'fr' ? 'CONCESSIONNAIRES' : 'DEALERS',
      description: language === 'fr' ? 'Partenariats de confiance' : 'Trusted partnerships'
    },
    {
      icon: <img src="/images/car.png" alt="Car" />,
      title: language === 'fr' ? 'CONDUCTEURS' : 'DRIVERS', 
      description: language === 'fr' ? 'Protection complète' : 'Complete protection'
    },
    {
      icon: <img src="/images/clip.png" alt="Clipboard" />,
      title: language === 'fr' ? 'RÉCLAMATIONS' : 'CLAIMS',
      description: language === 'fr' ? 'Service rapide et fiable' : 'Fast and reliable service'
    }
  ];

  return (
    <HeroContainer id="home">
      <HeroContent>
        <HeroTitle>
          {t.hero.title}
        </HeroTitle>
        <HeroSubtitle>
          {t.hero.subtitle}
        </HeroSubtitle>
        <HeroDescription>
          {t.hero.description}
        </HeroDescription>
        <HeroCTA onClick={scrollToServices}>
          {t.hero.cta}
        </HeroCTA>
      </HeroContent>

      <HeroFeatures>
        <FeaturesContainer>
          {features.map((feature, index) => (
            <FeatureItem key={index}>
              <FeatureCircle>
                {feature.icon}
              </FeatureCircle>
              <FeatureTitle>
                {feature.title}
              </FeatureTitle>
              <FeatureDescription>
                {feature.description}
              </FeatureDescription>
            </FeatureItem>
          ))}
        </FeaturesContainer>
      </HeroFeatures>
    </HeroContainer>
  );
};

export default Hero; 
